import { useEffect, useState } from 'react';
import { 
  Form, 
  Input, 
  Button, 
  Switch, 
  Radio, 
  InputNumber, 
  Card, 
  Typography, 
  Row, 
  Col, 
  Space, 
  Divider, 
  message,
  Select,
} from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import AxiosCaller from '../../../../authenticatie/AxiosCaller';
import { openNotificationSuccess, openNotificationError } from '../../../../Components/Notification/Notification';

const { Title } = Typography;
const { Option } = Select;


function MakenAanpassenLeverancier() {
    const { get, post, put } = AxiosCaller();
    const navigate = useNavigate();
    const [form] = Form.useForm();
    
    const location = useLocation();
    const leverancier = location.state;

    const initialState = {
        naam: '',
        bestelMethode: '',
        gesplitsteBestelling: false,
        adres: '',
        telefoon: '',
        email: '',
        btwnummer: '',
        website: '',
        bestelDagen: [],
        bestelTermijn: undefined,
        leverTermijn: undefined,
    };

    const [formData, setFormData] = useState(initialState);
    const [dagenOpties, setDagenOpties] = useState([]);
    const [termijnOpties, setTermijnOpties] = useState([]);
    const [aantal, setAantal] = useState('');
    const [selectedDagen, setSelectedDagen] = useState([]);
    const [selectedTermijnen, setSelectedTermijnen] = useState('');

    const [maxHeight, setMaxHeight] = useState(0);
        
    useEffect(() => {
        const updateHeight = () => {
            setMaxHeight(0.7 * document.documentElement.clientHeight);
        };

        updateHeight(); // Initialiseren bij eerste render
        window.addEventListener("resize", updateHeight); // Updaten bij schermresizing

        return () => window.removeEventListener("resize", updateHeight); // Cleanup
    }, []);
    
    const updateFields = (leverancierData) => {
        const data = {
            naam: leverancierData.naam,
            bestelMethode: leverancierData.bestelMethode,
            gesplitsteBestelling: leverancierData.gesplitsteBestelling,
            adres: leverancierData.contactGegevens?.adres || '',
            telefoon: leverancierData.contactGegevens?.telefoon || '',
            email: leverancierData.contactGegevens?.email || '',
            btwnummer: leverancierData.contactGegevens?.btwNummer || '',
            website: leverancierData.contactGegevens?.website || '',
            bestelDagen: leverancierData.bestelDagen || [],
            bestelTermijn: leverancierData.bestelTermijn?.naam || leverancierData.bestelTermijn || '',
            leverTermijn: leverancierData.leverTermijn || undefined,
        };
        
        setFormData(data);
        form.setFieldsValue(data);

        // Handle bestelDagen population
        if (Array.isArray(leverancierData.bestelDagen)) {
            const dagenValues = leverancierData.bestelDagen.map(dag => {
                return typeof dag === "string" ? dag : dag.naam;
            });
            form.setFieldValue('bestelDagen', dagenValues);
            setSelectedDagen(dagenValues);
        }

        // Set bestelTermijn correctly for both object and string id cases
        if (leverancierData.bestelTermijn) {
            if (typeof leverancierData.bestelTermijn === 'object' && leverancierData.bestelTermijn.naam) {
                setSelectedTermijnen(leverancierData.bestelTermijn);
                form.setFieldValue('bestelTermijn', leverancierData.bestelTermijn.naam);
            } else {
                // Store the ID reference for use in the submit handler
                setSelectedTermijnen({ _id: leverancierData.bestelTermijn });
                
                // Try to find the matching termijn once the options are loaded
                if (termijnOpties.length > 0) {
                    const matchingTermijn = termijnOpties.find(t => t._id === leverancierData.bestelTermijn);
                    if (matchingTermijn) {
                        form.setFieldValue('bestelTermijn', matchingTermijn.naam);
                        setSelectedTermijnen(matchingTermijn);
                    }
                }
            }
        }
        
        setAantal(leverancierData.leverTermijn || undefined);
        form.setFieldValue('leverTermijn', leverancierData.leverTermijn || undefined);
    };

    const fetchDagenEnTermijnen = async () => {
        try {
            const dagenResponse = await get('/leverancier/bestelDagen');
            const termijnResponse = await get('/bestelTermijn');
            
            setDagenOpties(dagenResponse.data);
            setTermijnOpties(termijnResponse.data);
            
            // If we already have a supplier with bestelTermijn as an ID, find the name
            if (leverancier && leverancier.bestelTermijn && typeof leverancier.bestelTermijn !== 'object') {
                const matchingTermijn = termijnResponse.data.find(t => t._id === leverancier.bestelTermijn);
                if (matchingTermijn) {
                    form.setFieldValue('bestelTermijn', matchingTermijn.naam);
                    setSelectedTermijnen(matchingTermijn);
                }
            }
        } catch (error) {
            console.error('Fout bij ophalen van bestelopties:', error);
            openNotificationError("Fout bij ophalen van bestelopties", error)
        }
    };

    useEffect(() => {
        fetchDagenEnTermijnen();
    }, []);

    useEffect(() => {
        if (leverancier) {
            updateFields(leverancier);
        }
    }, [leverancier, termijnOpties]);

    const handleDagenChange = (selectedValues) => {
        setSelectedDagen(selectedValues);
    };

    const handleTermijnenChange = (value) => {
        const termijn = termijnOpties.find(t => t.naam === value);
        setSelectedTermijnen(termijn);
    };
    

    const handleSubmit = async (values) => {
        try {
            if(!values.naam || !values.bestelMethode){
                openNotificationError("Naam en BestelMethode zijn verplicht")
                return;
            }

            if(!values.bestelDagen || values.bestelDagen.length === 0){
                openNotificationError("Selecteer minstens één besteldag")
                return;
            }

            if(!values.bestelTermijn){
                openNotificationError("Bestel termijn is verplicht")
                return;
            }

            if(values.leverTermijn === undefined || values.leverTermijn === null){
                openNotificationError("Levertermijn is verplicht")
                return;
            }

            if(values.leverTermijn <= 0){
                openNotificationError("Levertermijn moet groter zijn dan 0")
                return;
            }

            // Find the correct termijn object based on the selected name
            const selectedTermijn = termijnOpties.find(t => t.naam === values.bestelTermijn) || selectedTermijnen;
            
            const leverancierData = {
                naam: values.naam,
                bestelMethode: values.bestelMethode,
                gesplitsteBestelling: values.gesplitsteBestelling,
                contactGegevens: {
                    adres: values.adres || '',
                    telefoon: values.telefoon || '',
                    email: values.email || '',
                    btwNummer: values.btwnummer || '',
                    website: values.website || ''
                },
                bestelDagen: values.bestelDagen,
                bestelTermijn: selectedTermijn?._id,
                leverTermijn: values.leverTermijn
            };

            if (leverancier) {
                await put('/leverancier/' + leverancier._id, leverancierData);
               
            } else {
                await post('/leverancier', leverancierData);
                
            }

            openNotificationSuccess("Leverancier succesvol aangemaakt/aangepast")
            
            form.resetFields();
            navigate('/Admin/configuratie/Leverancier');
        } catch (error) {
            console.error('Error:', error);
            openNotificationError("Fout bij aanmaken/aanpassen leverancier", error)
        }
    };

    const handleCancel = () => {
        navigate('/Admin/configuratie/Leverancier');
    };

    return (
        <div className="Screen-container">
            <div className="title-container">
                <div className="flex gap">
                    <ArrowLeftOutlined className="back-arrow" onClick={() => navigate('/Admin/configuratie/Leverancier')} />
                    <span className="title">Leverancier</span>
                </div>
            </div>
            <div style={{ padding: '24px' }}>
                <Card>
                    <Form
                        form={form}
                        layout="vertical"
                        initialValues={initialState}
                        onFinish={handleSubmit}
                        style={{overflowY: "auto", maxHeight}}
                    >
                        <Row gutter={24}>
                            <Col span={8}>
                                <Form.Item 
                                    name="naam" 
                                    label="Naam" 
                                    rules={[{ required: true, message: 'Naam is verplicht' }]}
                                >
                                    <Input placeholder="Voer de naam in" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item 
                                    name="bestelMethode" 
                                    label="Bestelmethode" 
                                    rules={[{ required: true, message: 'Bestelmethode is verplicht' }]}
                                >
                                    <Input placeholder="Email, website, ..." />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item 
                                    name="gesplitsteBestelling" 
                                    label="Gesplitste bestelling?" 
                                    valuePropName="checked"
                                >
                                    <Switch />
                                </Form.Item>
                            </Col>
                        </Row>
    
                        <Divider />
    
                        <Row gutter={24}>
                            <Col span={8}>
                                <Form.Item 
                                    name="bestelDagen" 
                                    label="Bestel dagen" 
                                    rules={[{ required: true, message: 'Selecteer minstens één besteldag' }]}
                                >
                                    <Select
                                        mode="multiple"
                                        placeholder="Selecteer besteldagen"
                                        style={{ width: '100%' }}
                                        onChange={handleDagenChange}
                                        optionLabelProp="label"
                                    >
                                        {dagenOpties.map(dag => (
                                            <Option key={dag.naam} value={dag.naam} label={dag.naam}>
                                                {dag.naam}
                                            </Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item 
                                    name="bestelTermijn" 
                                    label="Bestel Termijnen"
                                    rules={[{ required: true, message: 'Bestel termijn is verplicht' }]}
                                >
                                    <Select onChange={handleTermijnenChange} placeholder="Selecteer een termijn">
                                        {termijnOpties.map(termijn => (
                                            <Select.Option key={termijn._id} value={termijn.naam}>
                                                {termijn.naam}
                                            </Select.Option>
                                        ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item 
                                    name="leverTermijn" 
                                    label="Levertermijn"
                                    rules={[
                                        { required: true, message: 'Levertermijn is verplicht' },
                                        { type: 'number', min: 1, message: 'Levertermijn moet groter zijn dan 0' }
                                    ]}
                                >
                                    <InputNumber 
                                        min={1} 
                                        placeholder="Aantal" 
                                        style={{ width: '100%' }}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>
    
                        <Divider />
    
                        <Row gutter={24}>
                            <Col span={8}>
                                <Form.Item name="adres" label="Adres">
                                    <Input placeholder="Voer het adres in" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="telefoon" label="Telefoon">
                                    <Input placeholder="Voer telefoonnummer in" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="email" label="Email">
                                    <Input placeholder="Voer emailadres in" />
                                </Form.Item>
                            </Col>
                        </Row>
    
                        <Row gutter={24}>
                            <Col span={8}>
                                <Form.Item name="btwnummer" label="BTW-nummer">
                                    <Input placeholder="Voer het BTW-nummer in" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item name="website" label="Website">
                                    <Input placeholder="Voer de website in" />
                                </Form.Item>
                            </Col>
                        </Row>
    
                        <Form.Item>
                            <Space>
                                <Button type="primary" htmlType="submit">
                                    {leverancier ? 'Bijwerken' : 'Aanmaken'}
                                </Button>
                                <Button onClick={handleCancel}>
                                    Annuleren
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </div>
    );
    
}

export default MakenAanpassenLeverancier;